import { AppConfigData } from '@mit/aws-vue';

type Stage = 'development' | 'dev' | 'test' | 'prod';

interface StageConfig {
    amplify: {
        domain: string;
        userPoolId: string;
        userPoolWebClientId: string;
        redirectUris: string[];
        postLogoutRedirectUris: string[];
    };
    okta: {
        issuer: string;
        clientId: string;
        redirectUris: string[];
        postLogoutRedirectUris: string[];
    };
    auth: {
        redirectUri: string;
        clientId: string;
        serverId: string;
    };
    sso: {
        authUrl: string;
        redirectUri: string;
        clientId: string;
        clientSecret?: string;
    };
}

const COMMON_SCOPES = {
    amplify: ['openid', 'profile', 'feature/user', 'covid19/user', 'digital-id/user', 'notification/user', 'unicard-kiosk/user', 'unicard-kiosk/system', 'user/all', 'user/admin.spaces'],
    okta: ['address', 'email', 'mit:user:all.read', 'openid', 'phone', 'profile', 'user/all', 'offline_access'],
    oauth: 'address email mit:user:all.read openid phone profile user/all offline_access',
    sso: 'openid+profile+feature/user+covid19/user+digital-id/user+notification/user+unicard-kiosk/user+unicard-kiosk/system+user/all+user/admin.spaces'
};

const STAGE_CONFIGS: Record<Stage, StageConfig> = {
    development: {
        amplify: {
            domain: 'atlas-auth-dev.mit.edu',
            userPoolId: 'us-east-1_14cFcpuS8',
            userPoolWebClientId: '3um7ugebmn59k6vsqiv572q361',
            redirectUris: ['http://localhost:9003'],
            postLogoutRedirectUris: ['http://localhost:9003/logout']
        },
        okta: {
            issuer: 'https://okta.mit.edu/oauth2/0oazz0oi2wANXoVfr697',
            clientId: '0oazz0oi2wANXoVfr697',
            redirectUris: ['http://localhost:9003'],
            postLogoutRedirectUris: ['http://localhost:9003']
        },
        auth: {
            redirectUri: 'http://localhost:9003',
            clientId: '0oazz0oi2wANXoVfr697',
            serverId: '0oazz0oi2wANXoVfr697'
        },
        sso: {
            authUrl: 'https://atlas-auth-dev.mit.edu',
            redirectUri: 'http://localhost:9003',
            clientId: '0oazz0oi2wANXoVfr697',
            clientSecret: 'oW1PczPlDlNDv_M_zM9v5gmt_i4mjEIPNAbpRZsRBhhfIOaJ6imERe1269YQ5817'
        }
    },
    dev: {
        amplify: {
            domain: 'atlas-auth-dev.mit.edu',
            userPoolId: 'us-east-1_14cFcpuS8',
            userPoolWebClientId: '3um7ugebmn59k6vsqiv572q361',
            redirectUris: ['https://arc-dashboard.atlas-apps-dev.mit.edu'],
            postLogoutRedirectUris: ['https://arc-dashboard.atlas-apps-dev.mit.edu']
        },
        okta: {
            issuer: 'https://okta.mit.edu/oauth2/aus6shg1319alZcDf697',
            clientId: '0oazz0oi2wANXoVfr697',
            redirectUris: ['https://arc-dashboard.atlas-apps-dev.mit.edu'],
            postLogoutRedirectUris: ['https://arc-dashboard.atlas-apps-dev.mit.edu']
        },
        auth: {
            redirectUri: 'https://arc-dashboard.atlas-apps-dev.mit.edu/',
            clientId: '0oazz0oi2wANXoVfr697',
            serverId: 'aus6shg1319alZcDf697'
        },
        sso: {
            authUrl: 'https://atlas-auth-dev.mit.edu',
            redirectUri: 'https://arc-dashboard.atlas-apps-dev.mit.edu',
            clientId: '0oazz0oi2wANXoVfr697'
        }
    },
    test: {
        amplify: {
            domain: 'atlas-auth-test.mit.edu',
            userPoolId: 'us-east-1_nY154N4yV',
            userPoolWebClientId: '1d35ud3ovq62b7sbht01cnvdfs',
            redirectUris: ['https://arc-dashboard.atlas-apps-test.mit.edu'],
            postLogoutRedirectUris: ['https://arc-dashboard.atlas-apps-test.mit.edu']
        },
        okta: {
            issuer: 'https://okta.mit.edu/oauth2/aus6shd5478WH30vd697',
            clientId: '0oatfelzg72g7ttmE697',
            redirectUris: ['https://arc-dashboard.atlas-apps-test.mit.edu'],
            postLogoutRedirectUris: ['https://arc-dashboard.atlas-apps-test.mit.edu']
        },
        auth: {
            redirectUri: 'https://arc-dashboard.atlas-apps-test.mit.edu/',
            clientId: '0oatfelzg72g7ttmE697',
            serverId: 'aus6shd5478WH30vd697'
        },
        sso: {
            authUrl: 'https://atlas-auth-test.mit.edu',
            redirectUri: 'https://arc-dashboard.atlas-apps-test.mit.edu',
            clientId: '1d35ud3ovq62b7sbht01cnvdfs'
        }
    },
    prod: {
        amplify: {
            domain: 'atlas-auth.mit.edu',
            userPoolId: 'us-east-1_bCu6BU8d2',
            userPoolWebClientId: '4q4d7uilvtood6rta91q7b6hjp',
            redirectUris: ['https://arc-dashboard.atlas-apps.mit.edu'],
            postLogoutRedirectUris: ['https://arc-dashboard.atlas-apps.mit.edu']
        },
        okta: {
            issuer: 'https://okta.mit.edu/oauth2/aus6sh93rjqnQuszg697',
            clientId: '0oawhq33lgLYNzhkG697',
            redirectUris: ['https://arc-dashboard.atlas-apps.mit.edu'],
            postLogoutRedirectUris: ['https://arc-dashboard.atlas-apps.mit.edu']
        },
        auth: {
            redirectUri: 'https://arc-dashboard.atlas-apps.mit.edu/',
            clientId: '0oawhq33lgLYNzhkG697',
            serverId: 'aus6sh93rjqnQuszg697'
        },
        sso: {
            authUrl: 'https://okta.mit.edu/oauth2/aus6sh93rjqnQuszg697',
            redirectUri: 'https://arc-dashboard.atlas-apps.mit.edu',
            clientId: '0oawhq33lgLYNzhkG697'
        }
    }
};

let currentStage: Stage = (import.meta.env.MODE as Stage) || 'development';

export function setAuthStage(stageName: string) {
    currentStage = stageName as Stage;
}

export function getAuthStage(): string {
    return currentStage;
}

function getStageConfig(stage: Stage = currentStage): StageConfig {
    return STAGE_CONFIGS[stage] || STAGE_CONFIGS.development;
}

export function getAppConfig(): AppConfigData {
    const stage = import.meta.env.MODE as Stage;
    const config = getStageConfig(stage);

    return {
        amplify: {
            oauth: {
                domain: config.amplify.domain,
                scope: COMMON_SCOPES.amplify,
                postLogoutRedirectUris: config.amplify.postLogoutRedirectUris,
                responseType: 'token',
                redirectUris: config.amplify.redirectUris,
                options: {
                    AdvancedSecurityDataCollectionFlag: false
                }
            },
            userPoolId: config.amplify.userPoolId,
            userPoolWebClientId: config.amplify.userPoolWebClientId
        },
        okta: {
            issuer: config.okta.issuer,
            clientId: config.okta.clientId,
            postLogoutRedirectUris: config.okta.postLogoutRedirectUris,
            redirectUris: config.okta.redirectUris,
            scopes: COMMON_SCOPES.okta
        },
        deployThisBuild: false,
        name: 'arc-dashboard-vue',
        stage: currentStage === 'development' ? 'dev' : currentStage,
        version: '0.1.0'
    };
}

export function getAuthValues() {
    const config = getStageConfig();

    return {
        clientId: config.auth.clientId,
        redirectUri: config.auth.redirectUri,
        serverId: config.auth.serverId,
        scopes: COMMON_SCOPES.oauth,
        stage: currentStage
    };
}

export function getSSOAuthValues() {
    const config = getStageConfig();

    return {
        clientId: config.sso.clientId,
        redirectUri: config.sso.redirectUri,
        serverId: config.auth.serverId,
        scopes: COMMON_SCOPES.sso,
        stage: currentStage,
        authUrl: config.sso.authUrl,
        clientSecret: config.sso.clientSecret || ''
    };
}
